import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { word } from "@/modules/core/utils/i18n"
import { SessionBlockEntityFactory } from "@/modules/sessions/entities/sessionBlockEntity"
import { getTemplateSessionValidationSchema } from "@/modules/sessions/programSessionFormikType"
import { ProgramInfiniteSessionTypeOf } from "@/modules/sessions/programSessionTypes"
import type { SessionBlockFormikType } from "@/modules/sessions/sessionForm/sessionBlockFormikTypes"
import type { TemplateSessionEntity } from "@/modules/template/entities/templateElementEntity"
import { useCreateSessionInTemplate, useEditSessionInTemplate } from "@/modules/template/hooks/useTemplate"
import dayjs from "dayjs"
import { useFormik } from "formik"
import { useCallback, useState } from "react"

export type TemplateSessionEditFormikType = {
  isTemplateSaved: boolean
  _type: typeof ProgramInfiniteSessionTypeOf
  name: string
  programId: string
  date: Date | null
  description: string | null
  blocks: SessionBlockFormikType[]
  hideSessionContent: boolean
  cardioTag: boolean
  weightliftingTag: boolean
  gymnasticTag: boolean
}

export const useTemplateSessionEditFormik = ({
  templateId,
  session,
  sessionIndex,
  onSuccess,
}: {
  templateId: string
  session?: TemplateSessionEntity
  sessionIndex?: number
  onSuccess?: () => void
}) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const { mutateAsync: editSessionInTemplate } = useEditSessionInTemplate()
  const { mutateAsync: createSessionInTemplate } = useCreateSessionInTemplate()

  const getInitialValues = useCallback((): TemplateSessionEditFormikType => {
    return {
      _type: ProgramInfiniteSessionTypeOf,
      name: session?.name ?? "",
      description: session?.description ?? "",
      date: null,
      programId: session?.programId ?? "",
      hideSessionContent: session?.hideSessionContent ?? false,
      cardioTag: session?.cardioTag ?? false,
      gymnasticTag: session?.gymnasticTag ?? false,
      weightliftingTag: session?.weightliftingTag ?? false,
      blocks: session?.blocks.map((block) => SessionBlockEntityFactory.fromEntityToFormik(block)) ?? [],
      isTemplateSaved: false,
    }
  }, [session])

  const formik = useFormik<TemplateSessionEditFormikType>({
    enableReinitialize: true,
    initialValues: getInitialValues(),
    validationSchema: getTemplateSessionValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true)
      try {
        const blocks = values.blocks.map((block, index) => SessionBlockEntityFactory.fromFormikToInputDto(block, index))

        if (session) {
          await editSessionInTemplate({
            templateId,
            sessionId: session.id,
            sessionInput: {
              name: values.name,
              description: values.description && values.description.length > 0 ? values.description : null,
              date: null,
              duration: 0,
              blocks,
              hideSessionContent: values.hideSessionContent,
              weightliftingTag: values.weightliftingTag,
              gymnasticTag: values.gymnasticTag,
              cardioTag: values.cardioTag,
              restDay: false,
              index: session.index,
              virtualProgramByDateIndex: session.virtualProgramByDateIndex,
            },
          })
        } else {
          if (sessionIndex === undefined) {
            throw new Error("sessionIndex is required to create a new session in template")
          }
          await createSessionInTemplate({
            templateId,
            sessionInput: {
              name: values.name,
              description: values.description && values.description.length > 0 ? values.description : null,
              date: dayjs().format("YYYY-MM-DD"),
              duration: 0,
              blocks,
              hideSessionContent: values.hideSessionContent,
              weightliftingTag: values.weightliftingTag,
              gymnasticTag: values.gymnasticTag,
              cardioTag: values.cardioTag,
              restDay: false,
              index: sessionIndex,
              virtualProgramByDateIndex: null,
            },
          })
        }

        setIsSubmitting(false)
        // TODO : changer le message du toast
        toast.success(session ? word("program.session.update.success") : word("program.session.create.success"), {
          classNames: successToastClassName(),
        })
        onSuccess?.()
        return true
      } catch (e) {
        setIsSubmitting(false)
        console.error(session ? "Template session edit submit error" : "Template session create submit error", e)
        toast.error(word("global.error.default"), {
          classNames: errorToastClassName(),
        })
        return false
      }
    },
  })

  return { formik, isSubmitting }
}
